import React, { useState, useMemo, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./DataTableTable";
import Dropdown, { DropdownOption } from "./DataTableDropdown";
import { ArrowDown2, SearchNormal1 } from "iconsax-reactjs";
import { type ClassValue, clsx } from "clsx"

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

export type SortDirection = "asc" | "desc" | null;

export interface SortOption {
  value: string;
  label: string;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface Column<T> {
  key: string;
  header: string;
  accessor?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: "left" | "right" | "center";
}

export interface DataTableProps<T> {
  // Data
  data: T[];
  columns: Column<T>[];

  // Title
  title?: string;

  // Search
  searchable?: boolean;
  searchPlaceholder?: string;
  searchKeys?: (keyof T)[];
  onSearchChange?: (searchValue: string) => void;
  searchValue?: string;

  // Sorting
  sortable?: boolean;
  sortOptions?: SortOption[];
  onSortChange?: (sortValue: string) => void;
  sortValue?: string;

  // Filtering
  filterable?: boolean;
  filterOptions?: FilterOption[];
  onFilterChange?: (filterValue: string) => void;
  filterValue?: string;
  filterPlaceholder?: string;

  // Actions
  renderActions?: (row: T, index: number) => React.ReactNode;

  // Row click
  onRowClick?: (row: T, index: number) => void;

  // Loading
  loading?: boolean;
  loadingText?: string;

  // Empty state
  emptyText?: string;

  // Pagination
  pagination?: boolean;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  showPageSizeSelector?: boolean;
  pageSizeOptions?: number[];
  totalItems?: number; // Total count for server-side pagination
  serverSidePagination?: boolean; // If true, pagination is handled server-side

  // Styling
  className?: string;
  headerClassName?: string;
  rowClassName?: string | ((row: T, index: number) => string);

  headerAction?: React.ReactNode;
}

export default function DataTable<T extends Record<string, any>>({
  data,
  columns,
  title,
  searchable = false,
  searchPlaceholder = "جستجو...",
  searchKeys,
  onSearchChange,
  searchValue: controlledSearchValue,
  sortable = false,
  sortOptions = [],
  onSortChange,
  sortValue: controlledSortValue,
  filterable = false,
  filterOptions = [],
  onFilterChange,
  filterValue: controlledFilterValue,
  filterPlaceholder = "فیلتر",
  renderActions,
  onRowClick,
  loading = false,
  loadingText = "در حال بارگزاری...",
  emptyText = "داده‌ای یافت نشد",
  pagination = false,
  pageSize = 10,
  currentPage: controlledCurrentPage,
  onPageChange,
  showPageSizeSelector = false,
  pageSizeOptions = [5, 10, 20, 50, 100],
  totalItems,
  serverSidePagination = false,
  className,
  headerClassName,
  rowClassName,
  headerAction,
}: DataTableProps<T>) {

  // Internal state for uncontrolled mode
  const [internalSearch, setInternalSearch] = useState("");
  const [internalSort, setInternalSort] = useState("");
  const [internalFilter, setInternalFilter] = useState("");
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(pageSize);
  const [columnSort, setColumnSort] = useState<{
    columnKey: string;
    direction: "asc" | "desc";
  } | null>(null);

  // Use controlled or uncontrolled values
  const searchValue =
    controlledSearchValue !== undefined
      ? controlledSearchValue
      : internalSearch;
  const sortValue =
    controlledSortValue !== undefined ? controlledSortValue : internalSort;
  const filterValue =
    controlledFilterValue !== undefined
      ? controlledFilterValue
      : internalFilter;
  const currentPage =
    controlledCurrentPage !== undefined
      ? controlledCurrentPage
      : internalCurrentPage;
  const effectivePageSize = internalPageSize;

  // Handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      if (onSearchChange) {
        onSearchChange(value);
      } else {
        setInternalSearch(value);
      }
    },
    [onSearchChange]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (value: string) => {
      if (onSortChange) {
        onSortChange(value);
      } else {
        setInternalSort(value);
      }
    },
    [onSortChange]
  );

  // Handle filter change
  const handleFilterChange = useCallback(
    (value: string) => {
      if (onFilterChange) {
        onFilterChange(value);
      } else {
        setInternalFilter(value);
      }
    },
    [onFilterChange]
  );

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    // Ensure data is always an array
    const safeData = Array.isArray(data) ? data : [];
    let result = [...safeData];

    // Apply search filter (only for client-side pagination)
    if (searchable && searchValue && !serverSidePagination) {
      const searchLower = searchValue.toLowerCase();
      result = result.filter((row) => {
        if (searchKeys && searchKeys.length > 0) {
          return searchKeys.some((key) => {
            const value = row[key];
            return value?.toString().toLowerCase().includes(searchLower);
          });
        }
        // Default: search in all string values
        return Object.values(row).some((value) =>
          value?.toString().toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply filter
    if (filterable && filterValue) {
      // Filter logic can be customized based on filterValue
      // This is a basic implementation
    }

    // Apply sorting from dropdown
    if (sortable && sortValue) {
      const [field, direction] = sortValue.startsWith("-")
        ? [sortValue.slice(1), "desc"]
        : [sortValue, "asc"];

      result.sort((a, b) => {
        const aValue = a[field];
        const bValue = b[field];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return direction === "asc"
            ? aValue.localeCompare(bValue, "fa")
            : bValue.localeCompare(aValue, "fa");
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          return direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });
    }

    // Apply column header sorting (takes priority over dropdown)
    if (columnSort) {
      const { columnKey, direction } = columnSort;
      result.sort((a, b) => {
        const aValue = a[columnKey];
        const bValue = b[columnKey];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return direction === "asc"
            ? aValue.localeCompare(bValue, "fa")
            : bValue.localeCompare(aValue, "fa");
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          return direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });
    }

    return result;
  }, [
    data,
    searchValue,
    filterValue,
    sortValue,
    columnSort,
    searchable,
    searchKeys,
    filterable,
    sortable,
  ]);

  // Calculate pagination
  const totalItemsCount =
    serverSidePagination && totalItems !== undefined && totalItems > 0
      ? totalItems
      : serverSidePagination
        ? filteredAndSortedData.length // Fallback to data length if totalItems not set
        : filteredAndSortedData.length;

  const totalPages =
    pagination && totalItemsCount > 0
      ? Math.max(1, Math.ceil(totalItemsCount / effectivePageSize))
      : 1;

  // Calculate start and end index for pagination info display
  const startIndex = pagination
    ? serverSidePagination
      ? (currentPage - 1) * effectivePageSize
      : (currentPage - 1) * effectivePageSize
    : 0;
  const endIndex = pagination
    ? serverSidePagination
      ? Math.min(currentPage * effectivePageSize, totalItemsCount)
      : Math.min(startIndex + effectivePageSize, filteredAndSortedData.length)
    : filteredAndSortedData.length;

  // For server-side pagination, use data as-is. For client-side, slice it.
  const paginatedData =
    pagination && !serverSidePagination
      ? filteredAndSortedData.slice(startIndex, endIndex)
      : filteredAndSortedData;

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      if (onPageChange) {
        onPageChange(page);
      } else {
        setInternalCurrentPage(page);
      }
    },
    [onPageChange]
  );

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setInternalPageSize(newPageSize);
      setInternalCurrentPage(1);
      if (onPageChange) {
        onPageChange(1);
      }
    },
    [onPageChange]
  );

  // Handle column header sort
  const handleColumnSort = useCallback(
    (columnKey: string) => {
      if (columnSort?.columnKey === columnKey) {
        // Toggle direction: asc -> desc -> null
        if (columnSort.direction === "asc") {
          setColumnSort({ columnKey, direction: "desc" });
        } else {
          setColumnSort(null);
        }
      } else {
        // New column, start with asc
        setColumnSort({ columnKey, direction: "asc" });
      }
      // Reset to first page when sorting
      setInternalCurrentPage(1);
      if (onPageChange) {
        onPageChange(1);
      }
    },
    [columnSort, onPageChange]
  );

  // Get sort icon for column
  const getSortIcon = (columnKey: string) => {
    if (columnSort?.columnKey === columnKey) {
      return columnSort.direction === "asc" ? (
        <ArrowDown2 size="16" color="#1dc399" variant="Outline" className="rotate-180" />
      ) : (
        <ArrowDown2 size="16" color="#1dc399" variant="Outline" />
      );
    }
    return <ArrowDown2 size="16" color="#9CA3AF" variant="Outline" className="rotate-90" />;
  };

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    handleSearchChange("");
    handleSortChange("");
    handleFilterChange("");
  }, [handleSearchChange, handleSortChange, handleFilterChange]);

  const hasActiveFilters = searchValue || sortValue || filterValue;

  return (
    <div
      className={cn(
        "w-full flex flex-col pt-4 px-4 border rounded-2xl card-shadow bg-white",
        className
      )}
      dir="rtl"
    >
      {/* Title and Search/Filter Bar */}
      <div className="flex flex-row items-center justify-between gap-4">
        {/* Title */}
        <div className="flex">
          <span className="w-[14/77px] border-[2.87px] rounded-full ml-2 border-primaryVar"></span>
          {title && (
            <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          )}
        </div>

        <div className="flex items-center gap-2">
          {headerAction}
        </div>

        {/* Search and Filter Bar */}
        {(searchable || sortable || filterable) && (
          <div className="flex flex-row items-center gap-2">
            {/* Clear Filters Button */}
            {hasActiveFilters && filterPlaceholder && (
              <button
                onClick={handleClearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap"
              >
                حذف فیلترها
              </button>
            )}
            {/* Filter Dropdown */}
            {filterable && filterOptions.length > 0 && (
              <div className="h-10 w-52 lg:w-32">
                <Dropdown<string>
                  options={[
                    ...(filterPlaceholder
                      ? [{ value: "", label: filterPlaceholder }]
                      : []),
                    ...filterOptions.map(
                      (opt) => (
                        { value: opt.value, label: opt.label } as DropdownOption<string>
                      )
                    ),
                  ]}
                  value={filterValue || ""}
                  onChange={(value) => handleFilterChange(value)}
                  className="w-full h-full"
                  renderButton={(option, isOpen, buttonRef, toggleOpen) => (
                    <button
                      ref={buttonRef}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOpen();
                      }}
                      className="w-full h-full text-primaryVar border border-primaryVar text-sm rounded-lg px-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="truncate">
                        {option?.label || filterPlaceholder}
                      </span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`transition-transform duration-200 flex-shrink-0 ${isOpen ? "rotate-180" : ""
                          }`}
                        style={{ fill: "currentColor" }}
                      >
                        <path d="M6 9L1 4h10z" />
                      </svg>
                    </button>
                  )}
                  menuWidth="200px"
                  placeholder={filterPlaceholder}
                />
              </div>
            )}
            {/* Search Input */}
            {searchable && (
              <div className="flex flex-row items-center border gap-3 rounded-lg h-10 py-1 px-3 w-72">
                <SearchNormal1
                  variant="Outline"
                  size={20}
                  color="#ADADAD"
                />
                <input
                  type="text"
                  className="w-52 outline-none text-sm placeholder-gray-300"
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            )}

            {/* Sort Dropdown */}
            {sortable && sortOptions.length > 0 && (
              <div className="w-52 lg:w-32 h-10">
                <Dropdown<string>
                  options={[
                    { value: "", label: "ترتیب نمایش" },
                    ...sortOptions.map(
                      (opt) =>
                      ({
                        value: opt.value,
                        label: opt.label,
                      } as DropdownOption<string>)
                    ),
                  ]}
                  value={sortValue || ""}
                  onChange={(value) => handleSortChange(value)}
                  className="w-full h-full"
                  renderButton={(option, isOpen, buttonRef, toggleOpen) => (
                    <button
                      ref={buttonRef}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOpen();
                      }}
                      className="w-full h-full text-primaryVar border border-primaryVar text-sm rounded-lg px-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="truncate">
                        {option?.label || "ترتیب نمایش"}
                      </span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`transition-transform duration-200 flex-shrink-0 ${isOpen ? "rotate-180" : ""
                          }`}
                        style={{ fill: "currentColor" }}
                      >
                        <path d="M6 9L1 4h10z" />
                      </svg>
                    </button>
                  )}
                  menuWidth="200px"
                  placeholder="ترتیب نمایش"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="relative w-full overflow-x-auto overflow-y-visible py-4">
        <Table className="table-fixed">
          <colgroup>
            {columns.map((column) => (
              <col
                key={column.key}
                style={
                  column.width ? { width: column.width } : { width: "auto" }
                }
              />
            ))}
            {renderActions && <col style={{ width: "120px" }} />}
          </colgroup>
          <TableHeader className={cn("bg-gray-50", headerClassName)}>
            <TableRow className="border-b-2 hover:bg-transparent h-12">
              {columns.map((column, index) => (
                <TableHead
                  key={column.key}
                  className={cn(
                    "text-sm font-medium text-gray-700",
                    column.align === "right" && "text-right",
                    column.align === "center" && "text-center",
                    column.align === "left" && "text-left",
                    !column.align && "text-right",
                    column.sortable !== false &&
                    "cursor-pointer select-none hover:bg-gray-100",
                    index === 0 && "rounded-r-xl",
                    index === columns.length - 1 &&
                    !renderActions &&
                    "rounded-l-xl"
                  )}
                  onClick={() => {
                    if (column.sortable !== false) {
                      handleColumnSort(column.key);
                    }
                  }}
                >
                  <div
                    className={cn(
                      "flex items-center gap-2",
                      column.align === "center" && "justify-center",
                      column.align === "left" &&
                      "justify-start flex-row-reverse",
                      (column.align === "right" || !column.align) &&
                      "justify-end flex-row-reverse"
                    )}
                  >
                    <span className="inline-flex items-center w-4 h-4 flex-shrink-0">
                      {column.sortable !== false && getSortIcon(column.key)}
                    </span>
                    <span className="truncate">{column.header}</span>
                  </div>
                </TableHead>
              ))}
              {renderActions && (
                <TableHead className="text-sm font-medium text-gray-700 text-center rounded-l-xl">
                  عملیات
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: pageSize }).map((_, rowIndex) => (
                <TableRow
                  key={`skeleton-${rowIndex}`}
                  className="border-t-2 h-12"
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      className={cn(
                        "py-2",
                        column.align === "right" && "text-right",
                        column.align === "center" && "text-center",
                        column.align === "left" && "text-left",
                        !column.align && "text-right"
                      )}
                    >
                      <div className="h-6 w-full rounded-md bg-gray-200 animate-pulse" />
                    </TableCell>
                  ))}

                  {renderActions && (
                    <TableCell className="py-3 text-center">
                      <div className="w-full h-full mx-auto rounded-md bg-gray-200 animate-pulse" />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow className="h-12">
                <TableCell
                  colSpan={columns.length + (renderActions ? 1 : 0)}
                  className="text-center py-8 text-gray-500"
                >
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const rowClass =
                  typeof rowClassName === "function"
                    ? rowClassName(row, index)
                    : rowClassName;

                return (
                  <TableRow
                    key={index}
                    className={cn(
                      "border-t-2 hover:bg-primaryVar-50 cursor-pointer transition-colors relative h-12",
                      rowClass
                    )}
                    style={{ overflow: "visible" }}
                    onClick={() => onRowClick?.(row, index)}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={cn(
                          "py-2 overflow-visible relative",
                          column.align === "right" && "text-right",
                          column.align === "center" && "text-center",
                          column.align === "left" && "text-left",
                          !column.align && "text-right"
                        )}
                        style={{ overflow: "visible" }}
                      >
                        <div className="truncate overflow-visible">
                          {column.accessor
                            ? column.accessor(row)
                            : (row[column.key] as React.ReactNode)}
                        </div>
                      </TableCell>
                    ))}
                    {renderActions && (
                      <TableCell
                        className="align-middle text-center py-2 px-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderActions(row, index)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {pagination && loading && (
          <div className="h-14" />
        )}
      </div>

      {/* Pagination */}
      {pagination &&
        (totalPages > 1 || (serverSidePagination && totalItemsCount > 0)) && (
          <div className="flex flex-row items-center justify-between gap-4 h-14">
            {/* Page Size Selector */}
            {showPageSizeSelector && (
              <div className="flex flex-row items-center gap-2">
                {/* <span className="text-sm text-gray-600">نمایش:</span> */}
                <select
                  value={effectivePageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm outline-none focus:border-primaryVar"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                {/* <span className="text-sm text-gray-600">مورد در هر صفحه</span> */}
              </div>
            )}

            {/* Pagination Info */}
            <div className="flex flex-row items-center gap-4">
              {/* <span className="text-sm text-gray-600">
                نمایش {startIndex + 1} تا {Math.min(endIndex, totalItemsCount)}{" "}
                از {totalItemsCount} مورد
              </span> */}

              {/* Pagination Controls */}
              <div className="flex flex-row items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={cn(
                    "px-3 py-1 border rounded-lg text-sm transition-colors",
                    currentPage === 1
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-primaryVar"
                  )}
                >
                  قبلی
                </button>

                {/* Page Numbers */}
                <div className="flex flex-row items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={cn(
                          "px-3 py-1 border rounded-lg text-sm transition-colors min-w-[36px]",
                          currentPage === pageNum
                            ? "bg-primaryVar text-white border-primaryVar"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-primaryVar"
                        )}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={cn(
                    "px-3 py-1 border rounded-lg text-sm transition-colors",
                    currentPage === totalPages
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-primaryVar"
                  )}
                >
                  بعدی
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};